import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/lib/auth";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/ProtectedRoute";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Catalog from "@/pages/Catalog";
import ProductDetail from "@/pages/ProductDetail";
import RepairPoints from "@/pages/RepairPoints";
import About from "@/pages/About";
import Diagnostic from "@/pages/Diagnostic";
import Checkout from "@/pages/Checkout";
import PaymentSuccess from "@/pages/PaymentSuccess";
import VippsMock from "@/pages/VippsMock";
import PaymentCancelled from "@/pages/PaymentCancelled";
import MyOrders from "@/pages/MyOrders";
import OrderTracking from "@/pages/OrderTracking";
import Account from "@/pages/Account";
import FixerDashboard from "@/pages/FixerDashboard";
import FixerJobs from "@/pages/FixerJobs";
import FixerJobDetail from "@/pages/FixerJobDetail";
import FixerProfile from "@/pages/FixerProfile";
import FixerInvoices from "@/pages/FixerInvoices";
import FixerInvoiceDetail from "@/pages/FixerInvoiceDetail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ForceChangePassword from "@/pages/ForceChangePassword";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminOrderDetail from "@/pages/AdminOrderDetail";
import AdminCustomerDetail from "@/pages/AdminCustomerDetail";
import AdminFixerDetail from "@/pages/AdminFixerDetail";
import FixerApplication from "@/pages/FixerApplication";

// Stable role arrays — extracted to module scope so they keep referential
// identity across renders (avoids re-mounting ProtectedRoute children).
const ROLE_CUSTOMER = ["customer"];
const ROLE_FIXER = ["fixer"];
const ROLE_ADMIN = ["admin"];

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Header />
          <main className="tk-fade-in min-h-[70vh]">
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/logg-inn" element={<Login />} />
              <Route path="/registrer" element={<Register />} />
              <Route path="/glemt-passord" element={<ForgotPassword />} />
              <Route path="/glemt-passord/:token" element={<ResetPassword />} />
              <Route path="/butikk" element={<Catalog />} />
              <Route path="/produkt/:id" element={<ProductDetail />} />
              <Route path="/verksted" element={<RepairPoints />} />
              <Route path="/om-oss" element={<About />} />
              <Route path="/diagnose" element={<Diagnostic />} />
              <Route path="/bli-verksted" element={<FixerApplication />} />
              <Route
                path="/kassen"
                element={
                  <ProtectedRoute roles={ROLE_CUSTOMER}>
                    <Checkout />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/betaling/vellykket"
                element={
                  <ProtectedRoute roles={ROLE_CUSTOMER}>
                    <PaymentSuccess />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/betaling/avbrutt"
                element={
                  <ProtectedRoute roles={ROLE_CUSTOMER}>
                    <PaymentCancelled />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/betaling/vipps-mock"
                element={
                  <ProtectedRoute roles={ROLE_CUSTOMER}>
                    <VippsMock />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mine-bestillinger"
                element={
                  <ProtectedRoute roles={ROLE_CUSTOMER}>
                    <MyOrders />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bestilling/:id"
                element={
                  <ProtectedRoute roles={ROLE_CUSTOMER}>
                    <OrderTracking />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/konto"
                element={
                  <ProtectedRoute roles={ROLE_CUSTOMER}>
                    <Account />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/verksted-dashbord"
                element={
                  <ProtectedRoute roles={ROLE_FIXER}>
                    <FixerDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/verksted/jobb/:id"
                element={
                  <ProtectedRoute roles={ROLE_FIXER}>
                    <FixerJobDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/verksted/profil"
                element={
                  <ProtectedRoute roles={ROLE_FIXER}>
                    <FixerProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/verksted/faktura"
                element={
                  <ProtectedRoute roles={ROLE_FIXER}>
                    <FixerInvoices />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/verksted/faktura/:id"
                element={
                  <ProtectedRoute roles={ROLE_FIXER}>
                    <FixerInvoiceDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/verksted/oppdrag"
                element={
                  <ProtectedRoute roles={ROLE_FIXER}>
                    <FixerJobs />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bytt-passord"
                element={
                  <ProtectedRoute>
                    <ForceChangePassword />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute roles={ROLE_ADMIN}>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/bestilling/:id"
                element={
                  <ProtectedRoute roles={ROLE_ADMIN}>
                    <AdminOrderDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/kunde/:id"
                element={
                  <ProtectedRoute roles={ROLE_ADMIN}>
                    <AdminCustomerDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/verksted/:id"
                element={
                  <ProtectedRoute roles={ROLE_ADMIN}>
                    <AdminFixerDetail />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </main>
          <Footer />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
